import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { api, formatApiError } from "../../api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "../ui/sonner";
import { formatCurrency } from "../../lib/format";
import { MONETIZATION } from "../../constants/testIds";
import { fadeUp } from "../../lib/motion";

// UX-CP-02 §2 SCR-P08-31 "Take-rate config" — POST already existed (Prompt 02), had no GET
// (added this prompt, additive/read-only). Per-gym, not platform-wide (SCHEMA.md's
// take_rate_config carries organization_id).
export function TakeRateConfigPanel({ gyms }) {
  const [orgId, setOrgId] = useState("");
  const [flatFee, setFlatFee] = useState("");
  const [minFloor, setMinFloor] = useState("");
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();

  const configQ = useQuery({
    queryKey: ["take-rate-config", orgId],
    queryFn: async () => (await api.get(`/control/gyms/${orgId}/take-rate-config`)).data,
    enabled: !!orgId,
  });

  useEffect(() => {
    if (configQ.data?.configured) {
      setFlatFee(String(configQ.data.flat_fee));
      setMinFloor(String(configQ.data.min_txn_floor));
    } else if (configQ.data) {
      setFlatFee("");
      setMinFloor("");
    }
  }, [configQ.data]);

  const saveMutation = useMutation({
    mutationFn: () => api.post("/control/take-rate-config", { organization_id: orgId, flat_fee: Number(flatFee), min_txn_floor: Number(minFloor) }),
    onSuccess: () => {
      toast.success("Take-rate config saved");
      queryClient.invalidateQueries({ queryKey: ["take-rate-config", orgId] });
    },
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <motion.div {...fadeUp(reducedMotion)} className="solid-surface rounded-[14px] p-5 space-y-4">
      <p className="eyebrow">Take-rate config</p>
      <div className="space-y-1.5">
        <Label>Gym</Label>
        <Select value={orgId} onValueChange={setOrgId}>
          <SelectTrigger data-testid={MONETIZATION.takeRateGymSelect}><SelectValue placeholder="Choose a gym…" /></SelectTrigger>
          <SelectContent>
            {(gyms || []).map((g) => <SelectItem key={g.organization_id} value={g.organization_id}>{g.display_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {orgId && (
        <>
          <p data-testid={MONETIZATION.takeRateCurrentValue} className="text-sm" style={{ color: "var(--scarlet-text-secondary)" }}>
            {configQ.isLoading ? "Loading current value…" : configQ.data?.configured
              ? `Current: flat fee ${formatCurrency(configQ.data.flat_fee)}, floor ${formatCurrency(configQ.data.min_txn_floor)}`
              : "Not configured yet for this gym."}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="trc-flat-fee">Flat fee (paise)</Label>
              <Input id="trc-flat-fee" type="number" min={0} data-testid={MONETIZATION.takeRateFlatFeeInput} value={flatFee} onChange={(e) => setFlatFee(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="trc-min-floor">Min txn floor (paise)</Label>
              <Input id="trc-min-floor" type="number" min={0} data-testid={MONETIZATION.takeRateFloorInput} value={minFloor} onChange={(e) => setMinFloor(e.target.value)} />
            </div>
          </div>
          <Button
            data-testid={MONETIZATION.takeRateSaveButton}
            disabled={saveMutation.isPending || flatFee === "" || minFloor === ""}
            onClick={() => saveMutation.mutate()}
            className="text-white"
            style={{ background: "var(--scarlet-accent)" }}
          >
            Save
          </Button>
        </>
      )}
    </motion.div>
  );
}
